import { watch, type Ref } from "vue";
import {
  canViewArticle,
  isTutorialInAppEnvironment,
  trainingTutorials,
  type AppEnvironment,
  type TrainingTutorial,
} from "@lemmonhq/knowledgebase";
import { evaluateCondition } from "./conditions";
import type { TrainingContext } from "./predicates";

// Staging/local count as "development"; only an explicit production build is "production".
const currentAppEnvironment: AppEnvironment =
  import.meta.env.VITE_ENV === "production" ? "production" : "development";

type Deps = {
  /** Reactive current route path. */
  routePath: Ref<string>;
  /** Builds a fresh context snapshot. */
  context: () => TrainingContext;
  /** True if a tutorial is already active (avoid double-start). */
  isActive: () => boolean;
  isCompleted: (id: string) => boolean;
  start: (id: string) => void;
};

const triggerType = (t: TrainingTutorial) => t.trigger?.type ?? "manual";

export function installTriggerController(deps: Deps) {
  const { routePath, context, isActive, isCompleted, start } = deps;

  const candidates = trainingTutorials.filter((t) => triggerType(t) !== "manual");

  // apps/web users are the "guest" knowledge-base role. A tutorial may auto-trigger only
  // when both its role visibility and environment visibility permit it — checked here so
  // an unavailable tutorial doesn't consume the single per-evaluation start slot.
  const isAvailable = (t: TrainingTutorial) =>
    canViewArticle(t.visibility, "guest") &&
    isTutorialInAppEnvironment(t, currentAppEnvironment);

  const tryTrigger = () => {
    if (isActive()) return;
    const ctx = context();
    for (const t of candidates) {
      const trig = t.trigger!;
      if (trig.route && trig.route !== ctx.route) continue; // exact path match only (no prefix/param matching yet)
      if (!isAvailable(t)) continue;
      if (isCompleted(t.id)) continue;
      if (!evaluateCondition(trig.condition, ctx)) continue;
      start(t.id);
      return; // start at most one per evaluation
    }
  };

  // Re-evaluate on route change. `{ immediate: true }` handles the initial load.
  // NOTE: only re-evaluates on route change — triggers whose condition depends on
  // non-route reactive state (user role, live DOM) will not fire mid-route. This is
  // correct for first_visit (a route-arrival event); always/condition-driven triggers
  // that must react to non-route state will need a separate observer.
  const stop = watch(routePath, tryTrigger, { immediate: true });
  return { stop, tryTrigger };
}
